import React, {useContext, useEffect, useRef, useState} from 'react';
import {AppContext} from '../context/AppContext';
import {JiraIssue} from '../types/components';
import FindExactToggle from './FindExactToggle';

interface IssueAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (issueKey: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  inputId?: string;
  required?: boolean;
  pattern?: string;
  autoComplete?: string;
}

const IssueAutocompleteInput: React.FC<IssueAutocompleteInputProps> = ({
  value,
  onChange,
  onSelect,
  onFocus,
  onBlur,
  placeholder = 'PROJ-123',
  inputId,
  required = true,
  pattern = '[A-Za-z][A-Za-z0-9_]+-[1-9][0-9]*',
  autoComplete = 'off'
}) => {
  const context = useContext(AppContext);
  if (!context) throw new Error('IssueAutocompleteInput must be used within AppContextProvider');
  const {searchIssues, i18n} = context;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [suggestions, setSuggestions] = useState<JiraIssue[]>([]);
  const [exact, setExact] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<number | null>(null);
  const seqRef = useRef(0);

  const queryIssues = async (q: string, isExact?: boolean) => {
    try {
      return await searchIssues(q, isExact ?? exact);
    } catch (e) {
      console.error('Jira search failed', e);
      return [];
    }
  };

  const debouncedSearch = (term: string, isExact?: boolean) => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    const q = term.trim();
    if (!q) {
      setLoading(false);
      setSuggestions([]);
      setOpen(false);
      setHighlight(-1);
      return;
    }
    setOpen(true);
    setLoading(true);
    const mySeq = ++seqRef.current;
    debounceRef.current = window.setTimeout(async () => {
      const items = await queryIssues(q, isExact);
      if (mySeq !== seqRef.current) return;
      setSuggestions(items);
      setLoading(false);
      setOpen(true);
      setHighlight(items.length ? 0 : -1);
    }, 300);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(t)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Raise the entire card above siblings when the dropdown is open
  useEffect(() => {
    const wrap = wrapperRef.current as HTMLElement | null;
    if (!wrap) return;

    let cardEl: HTMLElement | null = wrap;
    while (cardEl && !cardEl.classList.contains('card')) {
      cardEl = cardEl.parentElement;
    }
    if (!cardEl) return;

    if (open) {
      cardEl.classList.add('dropdown-active');
    } else {
      cardEl.classList.remove('dropdown-active');
    }
    return () => {
      cardEl.classList.remove('dropdown-active');
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="issue-autocomplete field relative">
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        pattern={pattern}
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v);
          debouncedSearch(v);
        }}
        onFocus={(e) => {
          if (value.trim()) {
            setOpen(true);
            debouncedSearch(value);
          }
          if (onFocus) onFocus(e);
        }}
        onBlur={(e) => {
          setOpen(false);
          if (onBlur) onBlur(e);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, Math.max(0, suggestions.length - 1)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            if (highlight >= 0 && suggestions[highlight]) {
              e.preventDefault();
              const key = suggestions[highlight].key;
              if (onSelect) onSelect(key);
              else onChange(key);
              setOpen(false);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      <FindExactToggle
        active={exact}
        onToggle={(val) => {
          setExact(val);
          if (value.trim()) {
            debouncedSearch(value, val);
          }
        }}
        tooltip={i18n.t('findExact')}
      />
      <div className={`suggestions ${open ? '' : 'hidden'}`}>
        {open && (
          <>
            {loading && (
              <div className="suggestion-item loading">
                <span className="spinner" aria-hidden="true"></span>
                {i18n.t('loading')}
              </div>
            )}
            {!loading && suggestions.length === 0 && value.trim() && (
              <div className="suggestion-item empty">
                {i18n.t('noResults')}
              </div>
            )}
            {!loading &&
              suggestions.map((s, idx) => (
                <div
                  key={s.key}
                  className={`suggestion-item ${idx === highlight ? 'active' : ''}`}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (onSelect) onSelect(s.key);
                    else onChange(s.key);
                    setOpen(false);
                  }}
                >
                  <strong>{s.key}</strong> — {s.summary || ''}
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
};

export default IssueAutocompleteInput;
